import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import API_URL from '../apiconfig';
import axios from './api';
import { auth, storage } from '../firebase';

interface ImageInput {
  id: string;
  url: string;
  path: string;
}

export const getSizeOptions = async () => {
  try {
    const response = await axios.get(`${API_URL}/v1/choices/tattoo`, {
      params: { category: 'TATTOO_SIZE' },
    });
    return response.data.result;
  } catch (error) {
    throw new Error('Cannot get size options', { cause: error });
  }
};

export const getLocationOptions = async () => {
  try {
    const response = await axios.get(`${API_URL}/v1/choices/tattoo`, {
      params: { category: 'PLACEMENT' },
    });
    return response.data.result;
  } catch (error) {
    throw new Error('Cannot get location options', { cause: error });
  }
};

export const getServices = async () => {
  try {
    const allOptions = await axios.get(`${API_URL}/v1/choices/tattoo`);

    const sizeOptions = allOptions.data.result.filter(
      (option: any) => option.category === 'TATTOO_SIZE',
    );

    return sizeOptions;
  } catch (error) {
    throw new Error('Cannot get services', { cause: error });
  }
};

export const getPiercingLocations = async () => {
  try {
    const piercingLocations = await axios.get(
      `${API_URL}/v1/choices/piercing-locations`,
    );

    return piercingLocations.data.result;
  } catch (error) {
    throw new Error('Cannot get piercing locations', { cause: error });
  }
};

export const uploadFiles = async (
  images: ImageInput[],
  {
    onSuccess,
  }: {
    onSuccess?: (image: ImageInput) => void;
  } = {},
) => {
  const user = auth.currentUser;
  const authServiceId = user?.uid;
  if (!authServiceId) throw new Error('Not authenticated');

  const transferPromises: Promise<string>[] = [];

  for (let i = 0; i < images.length; i += 1) {
    const image = images[i];

    const response = await fetch(image.url as URL | RequestInfo);
    // fetch() resolves on 4xx/5xx, so without this the error body would be
    // uploaded and getDownloadURL would hand back a valid-looking URL for what
    // is really a JSON error blob.
    if (!response.ok) {
      throw new Error(
        `Failed to read file for upload (${response.status} ${response.statusText})`,
      );
    }
    const blob = await response.blob();

    transferPromises.push(
      new Promise((resolve, reject) => {
        const storageUrl = image.path;
        const permanentImageRef = ref(storage, storageUrl);
        const uploadTask = uploadBytesResumable(permanentImageRef, blob);
        uploadTask.on(
          'state_changed',
          () => {},
          uploadError => reject(uploadError),
          async () => {
            try {
              // delete from idb if upload is successful
              onSuccess?.(image);
              const downloadURL = await getDownloadURL(permanentImageRef);
              resolve(downloadURL);
            } catch (completionError) {
              reject(completionError);
            }
          },
        );
      }),
    );
  }

  const imgUrls = await Promise.all(transferPromises);
  return imgUrls;
};

export type RemoteUploadResult = {
  url: string;
  contentType: string;
};

export const uploadBlobToPath = async (
  blob: Blob,
  path: string,
  contentType = 'image/jpeg',
): Promise<RemoteUploadResult> => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error('Not authenticated');

  const permanentImageRef = ref(storage, path);

  await new Promise<void>((resolve, reject) => {
    const uploadTask = uploadBytesResumable(permanentImageRef, blob, {
      contentType,
    });
    uploadTask.on(
      'state_changed',
      () => {},
      uploadError => reject(uploadError),
      () => resolve(),
    );
  });

  return {
    url: await getDownloadURL(permanentImageRef),
    contentType,
  };
};

export const uploadRemoteFileUrl = async (
  remoteUrl: string,
  path: string,
  contentType = 'image/jpeg',
): Promise<RemoteUploadResult> => {
  const response = await fetch(remoteUrl);
  if (!response.ok) {
    throw new Error('Failed to download remote file');
  }
  const blob = await response.blob();
  const resolvedContentType =
    blob.type?.split(';')[0] ||
    response.headers.get('content-type')?.split(';')[0] ||
    contentType;

  return uploadBlobToPath(blob, path, resolvedContentType);
};
